import React, { useState, useContext } from 'react';
import { FaUser, FaKey, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import css from './AccountDetails.module.css';
import { UserContext } from '../../../context/userContext';
import PasswordPolicy from '../../../components/Login/PasswordPolicy/PasswordPolicy';
import Validators from '../../../utility/validators';

interface AccountDetailsProps {
  user: any;
}

const AccountDetails: React.FC<AccountDetailsProps> = ({ user }) => {
  const { changePassword } = useContext(UserContext);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isSSO = user?.customData.companyId === '';

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset messages
    setPasswordError('');
    setPasswordSuccess('');
    
    // Validation
    if (!Validators.required(currentPassword)) {
      setPasswordError('Current password is required');
      return;
    }
    
    if (!Validators.passwordPolicy(newPassword)) {
      setPasswordError('New password must meet the password policy requirements');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Call the changePassword function from context
      const success = await changePassword!(currentPassword, newPassword);
      
      if (success) {
        setPasswordSuccess('Password successfully changed');
        // Reset form
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPasswordError('Failed to change password. Please check your current password and try again.');
      }
    } catch (error: any) {
      console.error('Password change detailed error:', error);
      setPasswordError(`An error occurred: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={css.accountDetails}>
      <div className={css.section}>
        <h2 className={css.sectionTitle}>
          <FaUser className={css.sectionIcon} />
          User Information
        </h2>
        <div className={css.userInfoContainer}>
          <div className={css.infoItem}>
            <span className={css.infoLabel}>Email:</span>
            <span className={css.infoValue}>{user?.customData.email || 'N/A'}</span>
          </div>
          {isSSO && (
            <div className={css.infoItem}>
              <span className={css.infoLabel}>Authentication Method:</span>
              <span className={css.infoValue}>Single Sign-On (SSO)</span>
            </div>
          )}
        </div>
      </div>

      {!isSSO && (
        <div className={css.section}>
          <h2 className={css.sectionTitle}>
            <FaKey className={css.sectionIcon} />
            Change Password
          </h2>
          <form onSubmit={handlePasswordReset} className={css.passwordForm}>
            {passwordError && (
              <div className={css.errorMessage}>
                <FaExclamationTriangle />
                <span>{passwordError}</span>
              </div>
            )}
            
            {passwordSuccess && (
              <div className={css.successMessage}>
                <FaCheck />
                <span>{passwordSuccess}</span>
              </div>
            )}
            
            <div className={css.passwordPolicy}>
              <PasswordPolicy />
            </div>
            
            <div className={css.formGroup}>
              <label htmlFor="currentPassword">Current Password</label>
              <input
                type="password"
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>
            
            <div className={css.formGroup}>
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>
            
            <div className={css.formGroup}>
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>
            
            <button 
              type="submit" 
              className={css.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>
        </div>
      )}
      
      {isSSO && (
        <div className={css.section}>
          <div className={css.ssoNotice}>
            <h3>Password Management Not Available</h3>
            <p>
              Your account is managed through Liberty's Single Sign-On (SSO) service. 
              Please <a href="mailto:it@libertyfrac.com">contact IT</a> if you need to change your password.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountDetails;